import React, { memo } from 'react'
import { FieldErrors, useWatch } from 'react-hook-form'
import ControlledAutocompleteTransfixUser from 'transfix-form/components/ControlledAutocompleteTransfixUser'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { useCreateShipmentFormContext } from '../../../context'
import { TAutocompleteValueSchema } from '../../../schema/AutocompleteValueSchema'
import { getAutocompleteErrorMessage } from '../../../utils'
import { IShipperInfoQuery, useShipperInfoQuery } from './graphql/ShipperInfo'

export interface IShipmentInfoFormSectionProps {}

const ShipmentInfoFormSection = memo<IShipmentInfoFormSectionProps>(() => {
  /**
   * Form Context
   */
  const { control, errors, setValue } = useCreateShipmentFormContext()

  /**
   * Ops Rep value
   */
  const opsRepValue = useWatch<TAutocompleteValueSchema>({ control, name: 'shipment.opsRep' })
  const { id: opsRepId } = opsRepValue || {}

  /**
   * Shippper Application value
   */
  const shipperApplicationValue = useWatch<TAutocompleteValueSchema>({
    control,
    name: 'shipperApplication',
  })
  const { id: shipperApplicationId } = shipperApplicationValue || {}

  /**
   * Set ops rep with shipper's carrier manager
   */
  const setOpsRep = (data: IShipperInfoQuery) => {
    const { shipperApplication } = data || {}
    const { carrierManager } = shipperApplication || {}
    const { fullName, id } = carrierManager || {}
    if (id) {
      setValue('shipment.opsRep', { id, text: fullName })
    }
  }

  /**
   * Fetch Shipper Info
   *
   * Skip condition:
   * skip if there is not a shipper selected OR an opsRep is already selected
   */
  useShipperInfoQuery({
    variables: {
      shipperId: shipperApplicationId || '',
    },
    skip: !shipperApplicationId || !!opsRepId,
    onCompleted: setOpsRep,
  })

  /**
   * Errors
   */
  const { shipment: shipmentErrors } = errors || {}
  const { opsRep } = shipmentErrors || {}
  const opsRepError = opsRep as FieldErrors<TAutocompleteValueSchema>

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant='h3'>Shipment Info</Typography>
      </Grid>
      <Grid item xs={12}>
        <ControlledAutocompleteTransfixUser
          control={control}
          name='shipment.opsRep'
          AutocompleteTransfixUserProps={{
            label: 'Ops Rep',
            dataTest: 'ops-rep-field',
            inputError: !!getAutocompleteErrorMessage(opsRepError, 'id'),
            inputValidationMessage: getAutocompleteErrorMessage(opsRepError, 'id'),
            required: true,
            size: 'small',
          }}
        />
      </Grid>
    </Grid>
  )
})

export default ShipmentInfoFormSection
